import { Injectable } from '@angular/core';

import { PromotionTokenType } from '@bpos';
import { DecisionSource, OfferDeclineReason, OverAskOfferResponse, OverAskOfferStatus, SlipOfferStatus } from '@bpos/v1/over-ask';
import { OverAskOfferStatus as CdsOverAskOfferStatus, OverAskOfferStatusDetails, OverAskOfferUpdateCommand } from '@cds/push/over-ask-commands';
import { EpcotConfig } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { ignoreElementsTyped } from '@frontend/sports/common/core/utils/rxjs';
import { CdsPushService, OverAskSubscription } from '@frontend/sports/content-distribution/feature';
import { ToasterService } from '@frontend/sports/toaster/feature';
import { IUserBettingSettings, IUserRootState, userBettingSettingsSelector } from '@frontend/sports/user/feature';
import { HtmlNode } from '@frontend/vanilla/core';
import { Actions, OnInitEffects, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { EMPTY, catchError, concatMap, filter, from, map, mergeMap, switchMap, take, takeUntil, tap, timer, withLatestFrom } from 'rxjs';

import { UiManagerActions } from '../../../../lib/ui-manager/ui-manager.actions';
import { UiStates } from '../../../../lib/ui-manager/ui-states.enum';
import BetslipActions from '../../../base/store/actions';
import { betslipSelector } from '../../../base/store/selectors';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipState } from '../../../core/betslip-state';
import { PickOddsState } from '../../../core/picks/pick-models';
import { OverAskBetslipError, OverAskFlowState, OverAskRejectReason } from '../../../model/over-ask/over-ask';
import { SingleSlipType } from '../../bet-generation/slip-type';
import BetslipPickActions from '../../picks/actions';
import { BetslipOverAskActions } from '../actions';
import { overAskFlowStateSelector, overAskStateSelector } from '../selectors';
import { OverAskApiService } from '../services/api-service';
import { OverAskRejectOfferModalDialogService } from '../services/over-ask-reject-offer-modal-dialog.service';

@Injectable()
export class OverAskEffects implements OnInitEffects {
    static readonly OVER_ASK_MODE_CLASS = 'app__over-ask-mode';
    static readonly EPCOT_ENABLED_CLASS = 'epcot-enabled';
    static readonly CHECK_OFFER_STATUS_INTERVAL = 20 * 1000;
    private readonly logger: SportsRemoteLogger;

    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private userStore: Store<IUserRootState>,
        private overAskRejectOfferDialog: OverAskRejectOfferModalDialogService,
        private overAskApiService: OverAskApiService,
        private cdsPush: CdsPushService,
        private htmlNodeService: HtmlNode,
        private toasterService: ToasterService,
        loggerFactory: LoggerFactory,
        private epcotConfig: EpcotConfig,
    ) {
        this.logger = loggerFactory.getLogger('OverAskEffects');
    }

    ngrxOnInitEffects(): Action {
        return BetslipOverAskActions.checkOfferStatus();
    }

    /**
     * When start flow:
     *    - Update ui state
     *    - Schedule pull offer check
     */
    handleOverAskStartFlow$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipOverAskActions.startFlow),
            concatMap((action) => {
                return [
                    BetslipActions.setState({ state: BetslipState.Edit }), // Set for now to edit, but remove in future
                    UiManagerActions.addState({ state: UiStates.OverAsk }),
                    BetslipOverAskActions.scheduleGetStatus({ offerId: action.offerId }),
                ];
            }),
        );
    });

    /**
     * When flow has finished
     *    - Update ui state
     */
    handleOverAskFinishFlow$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipOverAskActions.finishFlow),
            map(() => {
                return UiManagerActions.removeState({ state: UiStates.OverAsk });
            }),
        );
    });

    /**
     * When flow has finished or has trader offer
     * Clear toast message
     */
    clearToastMessage$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipOverAskActions.finishFlow, BetslipOverAskActions.traderHasCounterOffer),
                tap(() => {
                    this.toasterService.dismiss();
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    /**
     * When schedule pull
     *    - do a offer status pull after configured time.
     *    - cancel timer when either:
     *        - Flow has finished.
     *        - A push update has received
     */
    handleOverAskGetStatus$ = createEffect(() => {
        // Cancel schedule in case push update is received or over ask flow has finished
        const scheduleUntil = this.actions$.pipe(ofType(BetslipOverAskActions.finishFlow, BetslipOverAskActions.receivePushUpdate));

        return this.actions$.pipe(
            ofType(BetslipOverAskActions.scheduleGetStatus),
            mergeMap((action) =>
                timer(OverAskEffects.CHECK_OFFER_STATUS_INTERVAL).pipe(
                    map(() => BetslipOverAskActions.fetchStatus({ offerId: action.offerId })),
                    takeUntil(scheduleUntil),
                ),
            ),
        );
    });

    /**
     * When fetch status
     *    - Call overask controller to get current status
     */
    handleFetchOverAskStatus$ = createEffect(() => {
        const cancelAction = this.actions$.pipe(ofType(BetslipOverAskActions.receivePushUpdate));

        return this.actions$.pipe(
            ofType(BetslipOverAskActions.fetchStatus),
            withLatestFrom(this.userStore.select(userBettingSettingsSelector)),
            switchMap(([action, userSettingsState]) => {
                return this.overAskApiService.getStatus(action.offerId).pipe(
                    map((overAskOfferResponse) => this.processOverAskOffer(userSettingsState, overAskOfferResponse)),
                    takeUntil(cancelAction),
                );
            }),
        );
    });

    handleDeclineOffer$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipOverAskActions.rejectOffer),
                filter(
                    (action) =>
                        action.reason === OverAskRejectReason.CancelWaiting ||
                        action.reason === OverAskRejectReason.UserRejectOffer ||
                        action.reason === OverAskRejectReason.ArcUserProfileRestriction,
                ),
                concatLatestFrom(() => this.store.select(overAskStateSelector)),
                switchMap(([_, state]) => {
                    return this.overAskApiService.declineOffer(state.offerId);
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    /**
     * When trader has counteroffer:
     *    - Start countdown timer which runs down until offer expires.
     *        - When timer ticks update seconds
     *        - When timer reaches zero auto reject counteroffer.
     *    - Stop timer when offer is accepted or rejected.
     */
    handleTraderHasCounterOffer$ = createEffect(() => {
        const stopCountDown = this.actions$.pipe(ofType(BetslipOverAskActions.acceptOffer, BetslipOverAskActions.rejectOffer));

        return this.actions$.pipe(
            ofType(BetslipOverAskActions.traderHasCounterOffer),
            mergeMap((action) => {
                const expirationInSeconds = this.getExpiration(action.offerExpirationDateUtc);
                if (expirationInSeconds <= 0) {
                    return [BetslipOverAskActions.rejectOffer({ reason: OverAskRejectReason.AcceptOfferTimeout })];
                }

                return timer(0, 1000).pipe(
                    take(expirationInSeconds + 1),
                    map((num) => {
                        if (num < expirationInSeconds) {
                            return BetslipOverAskActions.acceptOfferTimeoutRemainingTime({ seconds: expirationInSeconds - num });
                        } else {
                            return BetslipOverAskActions.rejectOffer({ reason: OverAskRejectReason.AcceptOfferTimeout });
                        }
                    }),
                    takeUntil(stopCountDown),
                );
            }),
        );
    });

    /**
     * Finish flow when
     *    - Bet placement is started
     *    - Error occured during the over ask flow
     */
    finishFlow$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipOverAskActions.addError),
            map(() => BetslipOverAskActions.finishFlow()),
        );
    });

    /**
     * When the reject dialog is open we need to close in case of
     *        1. User opens the dialog in both pending or review screen but we finish the flow so -> Close dialog.
     *        2. Use opens the in pending screen, but gets a counter offer -> Close dialog in able to see the offer.
     */
    handleRejectionCloseDialog$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipOverAskActions.finishFlow, BetslipOverAskActions.traderHasCounterOffer),
                filter(() => this.overAskRejectOfferDialog.isOpen()),
                tap(() => this.overAskRejectOfferDialog.close()),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    /**
     * When user request reject offer
     *    - Show confirmation dialog for rejection
     */
    handleOverAskUserRequestRejectOffer$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipOverAskActions.userRequestRejectOffer),
            concatLatestFrom(() => this.store),
            mergeMap(([_, betslipRootState]) => {
                const inColumn = betslipRootState.ui.states.some((s) => s === UiStates.RightColumnVisible);

                return from(this.overAskRejectOfferDialog.open(inColumn)).pipe(
                    mergeMap(function* (result: string): Iterable<Action> {
                        if (result === 'CANCEL') {
                            const reason =
                                betslipRootState.betslip.overAskState.flowState === OverAskFlowState.Pending
                                    ? OverAskRejectReason.CancelWaiting
                                    : OverAskRejectReason.UserRejectOffer;
                            yield BetslipOverAskActions.rejectOffer({ reason });
                        }
                    }),
                    catchError(() => EMPTY),
                );
            }),
        );
    });

    /**
     * Handle push update events in V1 and V2 betslips
     * - Filter when over ask is active.en
     *    - Check if over ask pick state is different from original pick then mark it as the original pick
     */
    handlePickNotAvailable$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.updateV1Picks, BetslipPickActions.updateV2Picks),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([_, state]) => state.overAskState.flowState !== OverAskFlowState.None),
            // eslint-disable-next-line
            mergeMap(function* ([_, state]): Iterable<Action> {
                const overAskPicks = state.overAskState.bets.flatMap((b) => b.picks);

                for (const pick of state.picks.pickList) {
                    const overAskPick = overAskPicks.find((oa) => oa.pick.id.isEqual(pick.id));
                    if (overAskPick && overAskPick.oddsState !== pick.oddsState) {
                        yield BetslipOverAskActions.changePickStatus({
                            pickId: pick.id.toString(),
                            state: pick.oddsState,
                        });
                    }
                }
            }),
        );
    });

    /**
     * When over ask pick changes it's odd state
     * - Iterate through bets
     *        - If bet is single, and it's pick has changed send  BetslipOverAskActions.bettingOfferChanged
     *        - If bet is not single we can not remove it so reject offer
     */
    handleOverAskPickError$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipOverAskActions.changePickStatus),
            concatLatestFrom(() => this.store.select(overAskStateSelector)),
            // eslint-disable-next-line
            concatMap(([action, overAskState]) => {
                if (action.state !== PickOddsState.Open && overAskState.bets.some((b) => b.picks.some((p) => p.pick.id.isEqual(action.pickId)))) {
                    // If one of the picks is currently not available then
                    // when only one bet we will cancel it, but when multiple we will let it go
                    // because user can continue with the other bets.
                    if (overAskState.bets.length === 1) {
                        return [BetslipOverAskActions.rejectOffer({ reason: OverAskRejectReason.BettingOfferNotAvailable })];
                    } else {
                        return [BetslipOverAskActions.bettingOfferChanged({ hasChanges: true })];
                    }
                }

                return [];
            }),
        );
    });

    /**
     * When user accept betting offer changes
     *    - Remove closed/locked pick in single bet from the offer.
     *    - Update hasChanges flag to false
     */
    handleOverAskAcceptBettingOfferChanges$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipOverAskActions.acceptBettingOfferChanges),
            concatLatestFrom(() => this.store.select(overAskStateSelector)),
            // eslint-disable-next-line
            concatMap(function* ([_, overAskState]): Iterable<Action> {
                for (const bet of overAskState.bets) {
                    if (bet.type instanceof SingleSlipType && bet.picks[0].oddsState !== PickOddsState.Open) {
                        const pickId = bet.picks[0].pick.id;
                        yield BetslipOverAskActions.permanentRemoveBet({ pickId: pickId.toString() });
                        yield BetslipPickActions.removePick({ pickId });
                    }
                }
                yield BetslipOverAskActions.bettingOfferChanged({ hasChanges: false }); // Reset changes after accepting them.
            }),
        );
    });

    /**
     * Subscribe to cds push for offer updates, we start over ask flow
     */
    handleOverAskSubscribeToOfferUpdates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipOverAskActions.startFlow),
            mergeMap((action) => {
                const unsubscribe$ = this.actions$.pipe(ofType(BetslipOverAskActions.finishFlow));

                return this.cdsPush.subscribe$<OverAskOfferUpdateCommand>(OverAskSubscription.createKey(action.offerId)).pipe(
                    take(1),
                    map((command) => BetslipOverAskActions.receivePushUpdate({ command })),
                    takeUntil(unsubscribe$),
                );
            }),
        );
    });

    /**
     * Handle push updates for over ask.
     */
    handleReceivePushUpdate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipOverAskActions.receivePushUpdate),
            map((action) => {
                switch (action.command.offerStatus) {
                    case CdsOverAskOfferStatus.Accepted: {
                        if (
                            action.command.offerStatusDetails === OverAskOfferStatusDetails.AcceptUserOffer ||
                            action.command.offerStatusDetails === OverAskOfferStatusDetails.AcceptCounterOffer
                        ) {
                            return BetslipOverAskActions.fetchStatus({ offerId: action.command.overAskOfferId });
                        }
                        break;
                    }
                    case CdsOverAskOfferStatus.Declined: {
                        return BetslipOverAskActions.rejectOffer({ reason: this.convertToRejectStatus(action.command.offerStatusDetails) });
                    }
                }

                return undefined;
            }),
            filter(isDefined),
        );
    });

    /**
     * When ui state is changed
     *    - When OVER_ASK state is added
     *        - Add OVER_ASK_MODE_CLASS to the html element and EPCOT_ENABLED_CLASS when epcot is enabled
     *    - When OVER_ASK state is removed
     *        - Remove OVER_ASK_MODE_CLASS from the html element and EPCOT_ENABLED_CLASS when epcot is enabled
     */
    handleOverAskUiStateChanges$ = createEffect(
        () => {
            return this.store.select(overAskFlowStateSelector).pipe(
                tap((state) => {
                    this.htmlNodeService.setCssClass(OverAskEffects.OVER_ASK_MODE_CLASS, state !== OverAskFlowState.None);
                    this.htmlNodeService.setCssClass(
                        OverAskEffects.EPCOT_ENABLED_CLASS,
                        state !== OverAskFlowState.None && this.epcotConfig.isEnabled,
                    );
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    addError$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipOverAskActions.rejectOffer),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            map(([action, state]) => {
                if (
                    (action.reason === OverAskRejectReason.TraderRejectOffer || action.reason === OverAskRejectReason.BettingOfferNotAvailable) &&
                    state.overAskState.bets[0].promoTokens.some((token) => token.tokenType === PromotionTokenType.FreeBet)
                ) {
                    return BetslipOverAskActions.addError({ error: OverAskBetslipError.MaximumWinOfBetExceeded });
                }

                return BetslipOverAskActions.addError({ error: this.convertToError(action.reason) });
            }),
        );
    });

    handleOfferStatus$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipOverAskActions.checkOfferStatus),
            concatLatestFrom(() => this.store.select(overAskStateSelector)),
            filter(([_, state]) => state.flowState !== OverAskFlowState.None && !!state.offerId),
            map(([_, state]) => BetslipOverAskActions.fetchStatus({ offerId: state.offerId })),
        );
    });

    /**
     * Handle accept offer.
     *    We are going to handle accept Offer action which can be invoked from
     *    1. User click
     *    2. Push notification
     *    3. Get Status call
     *    We are going to do exhaustMap until betslip state is different of EDIT
     */
    handleAcceptOffer$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipOverAskActions.acceptOffer),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(([_, state]) => state.base.state === BetslipState.Edit),
            map(() => BetslipOverAskActions.placeBet()),
        );
    });

    /**
     * Handle trader accepted offer.
     *    We are going to dispatch an accept Offer action after the OA
     *    state has been updated with the offer data accepted by trader
     */
    handleTraderAcceptedOffer$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipOverAskActions.traderAcceptedOffer),
            map(() => BetslipOverAskActions.acceptOffer()),
        );
    });

    private processOverAskOffer(userSettings: IUserBettingSettings, overAskOffer?: OverAskOfferResponse): Action {
        try {
            const overAskStatusType = overAskOffer?.status;
            if (!overAskOffer || overAskStatusType === OverAskOfferStatus.Failed) {
                return BetslipOverAskActions.rejectOffer({ reason: OverAskRejectReason.TechnicalError });
            }

            if (overAskStatusType === OverAskOfferStatus.Pending) {
                return BetslipOverAskActions.scheduleGetStatus({ offerId: overAskOffer.overAskOfferId });
            }

            if (overAskStatusType === OverAskOfferStatus.Processed) {
                if (overAskOffer.offerDetails.betSlipOffers.every((o) => o.status === SlipOfferStatus.Accepted)) {
                    return BetslipOverAskActions.traderAcceptedOffer(overAskOffer.offerDetails);
                }
                if (overAskOffer.offerDetails.betSlipOffers.every((o) => o.status === SlipOfferStatus.Declined)) {
                    const declineReason = overAskOffer.offerDetails.betSlipOffers.find(
                        (o) => o.status === SlipOfferStatus.Declined,
                    )!.offerDeclineReason;
                    switch (declineReason) {
                        case OfferDeclineReason.DeclinedByTrader:
                            return BetslipOverAskActions.rejectOffer({ reason: OverAskRejectReason.TraderRejectOffer });
                        case OfferDeclineReason.ExpiredTraderDecision:
                            return BetslipOverAskActions.rejectOffer({ reason: OverAskRejectReason.BettingOfferNotAvailable });
                        default:
                            return BetslipOverAskActions.rejectOffer({ reason: OverAskRejectReason.TechnicalError });
                    }
                }

                if (
                    overAskOffer.offerDetails.decisionSource === DecisionSource.Auto &&
                    overAskOffer.offerDetails.betSlipOffers.some((o) => o.status === SlipOfferStatus.Modified) &&
                    userSettings.stakeChangeConsent
                ) {
                    return BetslipOverAskActions.traderAcceptedOffer(overAskOffer.offerDetails);
                }

                return BetslipOverAskActions.traderHasCounterOffer(overAskOffer.offerDetails);
            }
        } catch (e) {
            this.logger.error(e);
        }

        return BetslipOverAskActions.rejectOffer({ reason: OverAskRejectReason.TechnicalError });
    }

    private getExpiration(offerExpirationDateUtc?: string): number {
        if (!offerExpirationDateUtc) {
            return 10000000; // never expires ??
        }

        const diff = new Date(offerExpirationDateUtc).getTime() - Date.now();

        return Math.floor(diff / 1000);
    }

    private convertToError(reason: OverAskRejectReason): OverAskBetslipError {
        switch (reason) {
            case OverAskRejectReason.AcceptOfferTimeout:
            case OverAskRejectReason.BettingOfferNotAvailable:
                return OverAskBetslipError.OfferExpired;
            case OverAskRejectReason.CancelWaiting:
            case OverAskRejectReason.UserRejectOffer:
            case OverAskRejectReason.TraderRejectOffer:
                return OverAskBetslipError.OfferRejected;
            case OverAskRejectReason.PlaceBetFailed:
                return OverAskBetslipError.PlaceBetFailed;
            default:
                return OverAskBetslipError.FlowFailed;
        }
    }

    private convertToRejectStatus(status: OverAskOfferStatusDetails): OverAskRejectReason {
        switch (status) {
            case OverAskOfferStatusDetails.DeclinedByTrader:
                return OverAskRejectReason.TraderRejectOffer;
            case OverAskOfferStatusDetails.DeclinedByTimeout:
                return OverAskRejectReason.BettingOfferNotAvailable;
            default:
                return OverAskRejectReason.TechnicalError;
        }
    }
}
